import { lazy, useEffect, useMemo, useState } from 'react'
import { KeycloakAdminService } from '@nv/auth'
import { ActionDropdown, useNVModal } from '@nv/components'
import { ListTable } from '@nv/components'
import { AdminVoterService } from '@nv/services'
import { formatDateLocaleShort } from '@nv/utils'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import styles from './AdminElectorsList.module.scss'
const SuccessDownloaded = lazy(() => import('@nv/lazy-loadable/lib/modals/SuccessDownloaded'))
const Downloading = lazy(() => import('@nv/lazy-loadable/lib/modals/Downloading'))
const EditUser = lazy(() => import('@nv/lazy-loadable/lib/modals/EditUser'))
const UserAdvancedFilters = lazy(() => import('@nv/lazy-loadable/lib/modals/UserAdvancedFilters'))

const NUM_VOTERS_PER_PAGE = 10

export const AdminElectorsList = ({
  selectedColumns,
  selectedUserFilterTypes,
  showMemberTypeFilter = false,
  hasTitle = true,
  hasAdvancedFilters = false,
}) => {
  const { t, i18n } = useTranslation()
  const currentLanguage = i18n.language
  const [channelFilter] = useState('all')
  const [loading, setLoading] = useState(true)
  const [users, setUsers] = useState([])
  const [numUsers, setNumUsers] = useState(0)
  const [currentPageNum, setCurrentPageNum] = useState(0)
  const [userToEdit, setUserToEdit] = useState({ email: '' })
  const [editUserModalView, setEditUserModalView] = useState('Edit')
  const [filters, setFilters] = useState({})
  const [search, setSearch] = useState('')
  const [numRows, setNumRows] = useState(NUM_VOTERS_PER_PAGE)
  const { NVModal: SuccessDownloadedModal, openModal: openSuccessDownloadedModal } = useNVModal()
  const { NVModal: DownloadingModal, openModal: openDownloadingModal, closeModal: closeDownloadingModal } = useNVModal()
  const { NVModal: EditUserModal, openModal: openEditUserModal } = useNVModal()
  const { NVModal: UserAdvancedFiltersModal, openModal: openUserAdvancedFiltersModal } = useNVModal()

  const electorActionOptions = [
    { value: 'Edit', label: t('admin.dropdown.edit') },
    { value: 'Resend access email', label: t('admin.dropdown.resendAccessEmail') },
    { value: 'Reset password', label: t('admin.users.actions.resetPassword') },
  ]

  const columns = useMemo(() => {
    return [
      {
        id: 'Username',
        accessorKey: 'username',
        cell: (info) => info.getValue() || '',
        header: () => t('auth.login.labels.username.voter'),
      },
      {
        id: 'FullName',
        accessorKey: 'fullName',
        cell: (info) => info.getValue() || '',
        header: () => t('form.labels.fullName'),
      },
      {
        id: 'Email',
        accessorKey: 'email',
        cell: (info) => info.getValue() || '',
        header: () => t('form.labels.email'),
      },
      {
        id: 'Group',
        accessorKey: `group.abbreviation.${currentLanguage}`,
        cell: (info) => info.getValue() || '',
        header: () => t('filters.userFilters.group'),
      },
      {
        id: 'Subgroup',
        accessorKey: `subgroup.abbreviation.${currentLanguage}`,
        cell: (info) => info.getValue() || '',
        header: () => t('filters.userFilters.subgroup'),
      },
      {
        id: 'Region',
        accessorKey: `region.abbreviation.${currentLanguage}`,
        cell: (info) => info.getValue() || '',
        header: () => t('filters.userFilters.region'),
      },
      {
        id: 'Branch',
        accessorKey: `branch.abbreviation.${currentLanguage}`,
        cell: (info) => info.getValue() || '',
        header: () => t('filters.userFilters.branch'),
      },
      {
        id: 'UserType',
        accessorKey: 'userType',
        cell: (info) => (
          <span className={clsx(styles['type__label'], styles[`type__label--${info.getValue()?.toLowerCase()}`])}>
            {info.getValue() || ''}
          </span>
        ),
        header: () => t('filters.userFilters.type'),
      },
      {
        id: 'LastSignIn',
        accessorKey: 'lastSignIn',
        cell: (info) => (info.getValue() ? formatDateLocaleShort(info.getValue().createdAt, currentLanguage) : ''),
        header: () => t('admin.electorsList.lastSignIn'),
      },
      {
        id: 'Action',
        accessorKey: 'action',
        cell: (info) =>
          KeycloakAdminService.isSuperAdmin() || KeycloakAdminService.isContactInfoRoleAdmin() ? (
            <ActionDropdown
              values={electorActionOptions}
              onCallback={(value) => {
                setUserToEdit(info.row.original)
                setEditUserModalView(value)
                openEditUserModal()
              }}
            />
          ) : null,
        header: () => <span>{t('buttons.action')}</span>,
      },
    ].filter((column) => selectedColumns?.includes(column.id))
  }, [currentLanguage])

  const getUsers = async () => {
    setLoading(true)
    const resp = await AdminVoterService.fetchVotersPipsc(filters, channelFilter, numRows, currentPageNum * numRows, search)
    if (resp?.users) {
      setUsers(resp.users)
      setNumUsers(resp.numUsers)
    }
    setLoading(false)
  }

  useEffect(() => {
    getUsers()
  }, [filters, currentPageNum, currentLanguage, numRows])

  useEffect(() => {
    setCurrentPageNum(0)
    getUsers()
  }, [search])

  const getDowloadUrl = async () => {
    openDownloadingModal()
    const res = await AdminVoterService.getVoterCsvPipsc(filters, channelFilter, currentLanguage)
    if (res?.success) {
      // @ts-ignore
      const href = URL.createObjectURL(res.data)
      const link = document.createElement('a')
      link.download = `voterlist`
      link.href = href
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      closeDownloadingModal()
      openSuccessDownloadedModal()
    }
  }

  const refreshList = () => getUsers()

  return (
    <>
      <EditUserModal>
        <EditUser modalView={editUserModalView} userToEdit={userToEdit} onUpdate={() => getUsers()} />
      </EditUserModal>

      <DownloadingModal>
        <Downloading />
      </DownloadingModal>

      <SuccessDownloadedModal>
        <SuccessDownloaded />
      </SuccessDownloadedModal>

      {hasAdvancedFilters && (
        <UserAdvancedFiltersModal>
          <UserAdvancedFilters
            appliedFilters={filters}
            setAppliedFilters={setFilters}
            userFilterTypes={selectedUserFilterTypes}
            showMemberTypeFilter={showMemberTypeFilter}
          />
        </UserAdvancedFiltersModal>
      )}

      <div className={styles['container']}>
        {hasTitle && <h1 className={styles['header']}>{t('admin.users.electorsList')}</h1>}

        <ListTable
          data={users}
          columns={columns}
          currentPageNum={currentPageNum}
          dataLength={numUsers}
          onPageChange={setCurrentPageNum}
          loading={loading}
          showFilter
          download={KeycloakAdminService.isSuperAdmin() || KeycloakAdminService.isEventModuleAdmin() ? getDowloadUrl : null}
          onSearch={setSearch}
          searchValue={search}
          refreshList={refreshList}
          setFilterModal={openUserAdvancedFiltersModal}
          paginationLabel={t('admin.electorsList.electors')}
          searchPlaceholder={t('admin.electorsList.search')}
          noDataLabel={t('admin.electorsList.noElectorsFound')}
          onNumRowsChange={(numRows) => setNumRows(numRows)}
        />
      </div>
    </>
  )
}
